import * as React from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtCents } from "@/lib/time";
import { toast } from "sonner";
import { Users, Plus, X, Trash2 } from "lucide-react";
import { EmptyState } from "@/components/feel/EmptyState";
import DetailDrawer from "@/components/DetailDrawer";

/**
 * CustomersPanel — Books > Customers tab.
 *
 * Dense table + right drawer for create/edit.  Customer deletes are
 * refused server-side if the customer has open invoices (409
 * customer_has_invoices).  The panel surfaces that as a toast.
 */
export default function CustomersPanel({ companyId, canManage }) {
  const [customers, setCustomers] = React.useState(null);
  const [drawer, setDrawer] = React.useState(null); // {mode:'edit'|'new', row}
  const [q, setQ] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      const params = q ? { q } : {};
      const { data } = await api.get(`/companies/${companyId}/customers`, { params });
      setCustomers(data.customers || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load customers");
    }
  }, [companyId, q]);

  React.useEffect(() => { load(); }, [load]);

  if (customers === null) {
    return <div className="p-4 text-[12px] text-brand-muted">Loading…</div>;
  }

  return (
    <div data-testid="customers-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <input
          type="text"
          placeholder="Search customers…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="field-input h-8 max-w-[240px] text-[12.5px]"
          data-testid="customers-search"
        />
        {canManage && (
          <button className="btn-violet h-8 text-[12px]"
                  onClick={() => setDrawer({ mode: "new", row: null })}
                  data-testid="customers-new">
            <Plus size={12} /> New customer
          </button>
        )}
      </div>

      {customers.length === 0 ? (
        <EmptyState icon={Users} eyebrow="Customers"
                    title="No customers yet."
                    body={canManage
                      ? "Add customers you invoice regularly. Snapshot at issue keeps names honest even if you rename later."
                      : "Ask an admin to add customers."} />
      ) : (
        <div className="card-panel overflow-hidden">
          <table className="fable-table" data-testid="customers-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Address</th>
                <th className="w-[80px]"></th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`}
                    className="cursor-pointer" onClick={() => setDrawer({ mode: "edit", row: c })}>
                  <td className="font-medium text-brand-ink">{c.name}</td>
                  <td className="text-brand-muted mono-num text-[11.5px]">{c.email || "—"}</td>
                  <td className="text-brand-muted text-[11.5px] whitespace-pre-line max-w-[300px] truncate">
                    {c.address || "—"}
                  </td>
                  <td className="text-right">
                    <span className="text-[10.5px] text-brand-faint">Edit ›</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CustomerDrawer
        open={!!drawer}
        mode={drawer?.mode}
        row={drawer?.row}
        companyId={companyId}
        canManage={canManage}
        onClose={() => setDrawer(null)}
        onSaved={() => { setDrawer(null); load(); }}
      />
    </div>
  );
}

function CustomerDrawer({ open, mode, row, companyId, canManage, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = React.useState({ name: "", email: "", address: "", notes: "" });
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (open) {
      setForm({
        name: row?.name || "",
        email: row?.email || "",
        address: row?.address || "",
        notes: row?.notes || "",
      });
    }
  }, [open, row]);

  async function save() {
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim() || undefined,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null,
      };
      if (isEdit) {
        await api.patch(`/companies/${companyId}/customers/${row.id}`, payload);
      } else {
        if (!payload.name) { toast.error("Name is required"); setBusy(false); return; }
        await api.post(`/companies/${companyId}/customers`, payload);
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save customer");
    } finally { setBusy(false); }
  }

  async function del() {
    if (!window.confirm("Delete this customer? Only allowed if they have no open invoices.")) return;
    setBusy(true);
    try {
      await api.delete(`/companies/${companyId}/customers/${row.id}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Cannot delete");
    } finally { setBusy(false); }
  }

  return (
    <DetailDrawer open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <div className="p-4" data-testid="customer-drawer">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[13px] font-semibold text-brand-ink">
            {isEdit ? "Edit customer" : "New customer"}
          </div>
          <button className="btn-ghost h-7 w-7 !p-0" onClick={onClose}><X size={12}/></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="micro-label">Name</label>
            <input value={form.name} onChange={(e)=>setForm(f=>({...f,name:e.target.value}))}
                   disabled={!canManage} className="field-input" data-testid="customer-name"/>
          </div>
          <div>
            <label className="micro-label">Email</label>
            <input value={form.email} onChange={(e)=>setForm(f=>({...f,email:e.target.value}))}
                   disabled={!canManage} className="field-input" data-testid="customer-email"/>
          </div>
          <div>
            <label className="micro-label">Billing address</label>
            <textarea value={form.address} onChange={(e)=>setForm(f=>({...f,address:e.target.value}))}
                      disabled={!canManage} rows={3} className="field-input"
                      data-testid="customer-address"/>
          </div>
          <div>
            <label className="micro-label">Notes</label>
            <textarea value={form.notes} onChange={(e)=>setForm(f=>({...f,notes:e.target.value}))}
                      disabled={!canManage} rows={2} className="field-input"
                      data-testid="customer-notes"/>
          </div>
          {canManage && (
            <div className="flex items-center gap-2 pt-2 border-t border-brand-rule">
              <button className="btn-violet flex-1" onClick={save} disabled={busy}
                      data-testid="customer-save">
                {busy ? "…" : (isEdit ? "Save" : "Create")}
              </button>
              {isEdit && (
                <button className="btn-ghost h-9 text-[11.5px] text-coral" onClick={del} disabled={busy}
                        data-testid="customer-delete">
                  <Trash2 size={12}/> Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </DetailDrawer>
  );
}
